import { Link, Navigate, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslations } from "next-intl";
import { LocaleSwitcher } from "@/components/locale-switcher";
import { PageShell } from "@/components/ui";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";
import { isSelfServeSignupEnabled } from "@lms/backend/lib/platform-admin/self-serve-signup";

export function PlatformSignupForm() {
  const t = useTranslations("platformSignup");
  const tRoot = useTranslations();
  const navigate = useNavigate();
  const [orgName, setOrgName] = useState("");
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await apiFetch("/v1/platform/signup", {
        method: "POST",
        body: JSON.stringify({
          name: orgName.trim(),
          slug: slug.trim(),
          adminName: name.trim(),
          adminEmail: email.trim(),
          adminPassword: password,
        }),
      });
      if (!res.ok) {
        // R3-O7 (C4): a translated message for the code, never the server's English.
        setError(
          apiErrorText(await readApiErrorResponse(res), tRoot, "platformSignup.signupFailed", {
            namespace: "platformSignup.errors",
            // SPA-10: a refused password names the rule, not just "details aren't valid".
            fields: { adminPassword: "auth.passwordHintLong" },
          }),
        );
        return;
      }
      const json = (await res.json()) as { data?: { tenant?: { slug?: string } } };

      const tenantSlug = json.data?.tenant?.slug;
      if (!tenantSlug) {
        setError(t("signupFailed"));
        return;
      }

      navigate(`/t/${tenantSlug}/login?signup=1`);
    } catch (err) {
      setError(apiErrorText(err, tRoot, "platformSignup.signupFailed"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={(event) => void handleSubmit(event)} className="mx-auto max-w-md space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t("title")}</h1>
          <p className="mt-1 text-sm text-[var(--text-muted)]">{t("subtitle")}</p>
        </div>
        <LocaleSwitcher />
      </div>

      <label className="block text-sm">
        {t("orgNameLabel")}
        <input
          data-testid="platform-signup-org-name"
          className="mt-1 w-full rounded border px-3 py-2"
          value={orgName}
          onChange={(event) => {
            setOrgName(event.target.value);
            if (!slug) {
              setSlug(
                event.target.value
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "-")
                  .replace(/^-|-$/g, ""),
              );
            }
          }}
          required
        />
      </label>

      <label className="block text-sm">
        {t("slugLabel")}
        <input
          data-testid="platform-signup-slug"
          className="mt-1 w-full rounded border px-3 py-2"
          value={slug}
          onChange={(event) => setSlug(event.target.value)}
          required
        />
      </label>

      <label className="block text-sm">
        {t("yourNameLabel")}
        <input
          data-testid="platform-signup-name"
          className="mt-1 w-full rounded border px-3 py-2"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
        />
      </label>

      <label className="block text-sm">
        {t("adminEmailLabel")}
        <input
          data-testid="platform-signup-email"
          type="email"
          className="mt-1 w-full rounded border px-3 py-2"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
      </label>

      <label className="block text-sm">
        {t("passwordLabel")}
        <input
          data-testid="platform-signup-password"
          type="password"
          autoComplete="new-password"
          aria-describedby="platform-signup-password-hint"
          className="mt-1 w-full rounded border px-3 py-2"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />
        {/* SPA-10: the rule up front, not only after a refusal. */}
        <span
          id="platform-signup-password-hint"
          data-testid="platform-signup-password-hint"
          className="mt-1 block text-xs text-[var(--text-muted)]"
        >
          {tRoot("auth.passwordHintLong")}
        </span>
      </label>

      {error ? (
        <p className="text-sm text-red-600" role="alert" data-testid="platform-signup-error">
          {error}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={loading}
        data-testid="platform-signup-submit"
        className="rounded bg-[var(--ink-9)] px-4 py-2 text-sm text-white disabled:opacity-50"
      >
        {loading ? t("creating") : t("createTenant")}
      </button>

      <p className="text-sm text-[var(--text-muted)]">
        {t("alreadyHaveAccount")}{" "}
        <Link to="/" className="underline" data-testid="platform-signup-sign-in">
          {t("signIn")}
        </Link>
      </p>
    </form>
  );
}

export function SignupPage() {
  // SEC-3: public tenant signup only exists when the build opts in.
  if (!isSelfServeSignupEnabled(import.meta.env.VITE_SELF_SERVE_SIGNUP)) {
    return <Navigate to="/" replace />;
  }
  return (
    <PageShell data-testid="platform-signup-page" className="py-10">
      <PlatformSignupForm />
    </PageShell>
  );
}
